import { PlayerError } from '@foundation-player/error-codes';
import { ManifestType, SourceType, StreamType } from '@foundation-player/runtime';
import type { Manifest, MediaConfig, MediaMetadata, Source } from '@foundation-player/types';
import { v4 as uuidv4 } from 'uuid';
import { PlayerEvent } from './event-emitter/events';
import { FoundationErrorEvent } from './event-emitter/unfiltered';

type MediaType = Pick<Required<MediaConfig>, 'type' | 'startTime' | 'muted' | 'playbackSpeed'> &
  Pick<Partial<MediaConfig>, 'id' | 'audioLanguage' | 'textTrackLanguage' | 'metadata'> & {
    /**
     * A UUIDv4 to uniquely identify the media session
     */
    sessionId: string;

    /**
     * A UUIDv4 to uniquely identify the media session
     *
     * @remarks
     * If no contentId is provided, a new UUIDv4 will be generated.
     */
    contentId?: string;

    /**
     * The Manifest to select a source from
     */
    manifest: Manifest;

    /**
     * The Source to use for playback
     */
    source: Source;

    /**
     * Determine whether a user is allowed to access OV audio.
     * If allowOvAudio is false only german audios are allowed to be accessed.
     * If there are multiple german audio tracks all of them are allowed, even if allowOvAudio == false.
     * Subtitles are not influenced by this.
     */
    ovAudio?: boolean;

    /**
     * Whether the media should start playing automatically
     */
    autoplayRequested: boolean;

    /**
     * Whether the media can start playing automatically
     */
    autoplaySupported: boolean;
  };

/**
 * DTO for content playback
 *
 * @remarks
 * The goal of this type is to define a structure for player components to play content,
 * without doing any further guesswork as to which content should be played or how.
 *
 * @internal
 */
export class Media implements MediaType {
  public readonly id?: string;
  public readonly sessionId: string;
  public readonly contentId: string;

  public readonly autoplayRequested: boolean;
  public readonly autoplaySupported: boolean;
  public readonly muted: boolean;
  public readonly startTime: number;
  public readonly playbackSpeed: number;
  public readonly type: SourceType;
  public readonly source: Source;
  public readonly manifest: Manifest;
  public readonly ovAudio: boolean;
  public readonly metadata: MediaMetadata;

  constructor(data: MediaType) {
    this.id = data.id;
    this.sessionId = data.sessionId;
    this.contentId = data.contentId ?? uuidv4();

    this.type = data.type;
    this.autoplayRequested = data.autoplayRequested;
    this.autoplaySupported = data.autoplaySupported;
    this.muted = data.muted;
    this.startTime = data.startTime;
    this.playbackSpeed = data.playbackSpeed;
    this.manifest = data.manifest;
    this.source = data.source;
    // In general, we want to allow OV audio. OV audio should only be disabled if we receive an explicit response from the backend.
    // Thus, this flag may later be changed by a plugin.
    this.ovAudio = data.ovAudio ?? true;

    this.metadata = data.metadata || {};
  }

  get autoplay(): boolean {
    return this.autoplayRequested && this.autoplaySupported;
  }

  getStreamType(): StreamType {
    if (([ManifestType.HlsHD, ManifestType.HlsSD] as ManifestType[]).includes(this.manifest.type)) {
      return StreamType.HLS;
    }

    if (([ManifestType.DashHD, ManifestType.DashSD] as ManifestType[]).includes(this.manifest.type)) {
      return StreamType.DASH;
    }

    if (([ManifestType.Progressive] as ManifestType[]).includes(this.manifest.type)) {
      return StreamType.Progressive;
    }

    throw new FoundationErrorEvent(PlayerEvent.Error, {
      message: `Could not determine stream type for manifest type "${this.manifest.type}"!`,
      code: PlayerError.VIDEOSOURCES_CONFIG_34200.code,
      context: {
        manifest: this.manifest,
      },
    });
  }
}
